import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Input  from "@react/shared/ui/Input"
import ManagerAutocomplete from "./ManagerAutocomplete"

const today = () => {
  const date  = new Date()
  const year  = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day   = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const INVOICING_METHODS = ["situation", "standard"]

const CreateProjectModal = ({ isOpen, onClose, eyebrow, convertUrl, managers, currentUserId, defaultStartDate }) => {
  const [managerId, setManagerId] = useState(currentUserId || "")
  const [startDate, setStartDate] = useState(defaultStartDate || today())
  const [endDate,   setEndDate]   = useState("")
  const [invoicingMethod, setInvoicingMethod] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setManagerId(currentUserId || "")
    setStartDate(defaultStartDate || today())
    setEndDate("")
    setInvoicingMethod("")
    setSubmitting(false)
  }, [isOpen, currentUserId, defaultStartDate])

  // ISO dates compare as strings.
  const endBeforeStart = startDate && endDate && endDate < startDate
  const canSubmit = managerId && startDate && endDate && !endBeforeStart && invoicingMethod && !submitting

  const handleConfirm = () => {
    if (!canSubmit) return
    setSubmitting(true)
    postRequest({
      url:  convertUrl,
      body: { project_manager_id: managerId, start_date: startDate, end_date: endDate, invoicing_method: invoicingMethod },
    })
      .then(response => {
        if (!response || response.errors) {
          flashAlert(response?.errors ? (Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors)) : I18n.t("quotes.builder.footer.unknown_error"))
          setSubmitting(false)
          return
        }
        if (response.panorama_url) {
          if (response.notice) flashNotice(response.notice)
          window.location.href = response.panorama_url
        } else {
          setSubmitting(false)
          onClose()
        }
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
        setSubmitting(false)
      })
  }

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      size    = "lg"
      eyebrow = {eyebrow}
      title   = {I18n.t("quotes.bigcard.convert_to_project")}
      footer  = {
        <>
          <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!canSubmit}>
            <FontAwesomeIcon icon={submitting ? "spinner" : "hard-hat"} spin={submitting} />
            {I18n.t("button.create")}
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">
          {I18n.t("projects.manager")}
        </label>
        <ManagerAutocomplete managers={managers || []} value={managerId} onChange={setManagerId} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {I18n.t("projects.start_date")}
          </label>
          <Input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {I18n.t("projects.end_date")}
          </label>
          <Input type="date" value={endDate} min={startDate || undefined} onChange={event => setEndDate(event.target.value)} />
          {endBeforeStart && <p className="text-xs text-rose-600">{I18n.t("projects.end_before_start")}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">
          {I18n.t("projects.invoicing_method")}
        </label>
        <select
          value     = {invoicingMethod}
          onChange  = {event => setInvoicingMethod(event.target.value)}
          className = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
        >
          <option value="" disabled>{I18n.t("projects.invoicing_method_prompt")}</option>
          {INVOICING_METHODS.map(method => (
            <option key={method} value={method}>{I18n.t(`projects.invoicing_methods.${method}`)}</option>
          ))}
        </select>
      </div>
    </Modal>
  )
}

export default CreateProjectModal
